import React, { useEffect, useState } from 'react';
import _get from 'lodash/get';
import { Button, Collapse, Typography, makeStyles } from '@material-ui/core';
import Mixpanel from 'utils/mixpanelService';
import { getDurationLabel } from 'utils/stringUtils';
import { SeriesSmallCard } from 'containers/Series/components/SeriesSmallCard';
import { CourseDefaultCard } from './CourseDefaultCard';
import { ArrowUpIcon } from './ArrowUpIcon';
import { isItemDisabled, showRecommendedTab } from '../utils';

const useStyles = makeStyles(theme => ({
  queueList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
    margin: '10px 0px 0px',
    padding: 0,
    listStyle: 'none',
  },
  wrapperInner: {
    display: 'flex',
    flexDirection: 'column',
    gap: 5,
    padding: '20px 0px 0px',
  },
  seriesContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer',

    '&[aria-checked="true"]': {
      '& svg': {
        transform: 'rotate(180deg)',
      },
    },

    '& svg': {
      transition: '0.3s linear',
    },
  },
  seriesTitle: {
    ...theme.typography.h6,
    fontSize: '1rem',
  },
  seriesTime: {
    ...theme.typography.pxSmall,
  },
  lengthContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  recommendedButton: {
    marginLeft: 20,
    marginTop: 10,
    justifyContent: 'flex-start',
  },
}));

export const ModuleQueue = ({
  course,
  activeModule,
  activeResourceIdx,
  resourceItemPageSiteCopy,
  coursesSiteCopy,
  activeSeries,
  onResourceClick,
  seriesProgress,
  completedSeriesIds,
  onRecommendedTabClick,
  onSeriesClick,
  trackingPath,
}) => {
  const classes = useStyles();

  const activeSeriesSlug = _get(activeSeries, 'fields.slug');
  const [expandedSeries, setExpandedSeries] = useState(activeSeriesSlug);
  const seriesCompletionOrder = _get(
    course.fields,
    'context.seriesCompletionOrder',
  );
  const moduleSeries = _get(activeModule.fields, 'moduleResources', []);
  const completedSeriesIndexes = completedSeriesIds.map(id =>
    moduleSeries.findIndex(item => item.sys.id === id),
  );

  useEffect(() => {
    setExpandedSeries(activeSeriesSlug);
  }, [activeSeriesSlug]);

  const onSeriesCollapse = (series, seriesIdx, isSeriesDisabled) => {
    const slug = _get(series, 'fields.slug');
    const isSameSlug = expandedSeries === slug;
    setExpandedSeries(isSameSlug ? null : slug);

    if (!isSeriesDisabled) {
      onSeriesClick(series, seriesIdx);
      if (!isSameSlug) {
        Mixpanel.track('Course - Module Section - Selected', {
          path: trackingPath,
          course: course.fields.title,
          module: activeModule.fields.title,
          section: series.fields.title,
        });
      }
    }
  };

  const handleSeriesResourceClick = seriesIndex => (resourceIdx, completed) =>
    onResourceClick(seriesIndex, resourceIdx, completed);

  const shouldDisableSeries = seriesIndex =>
    seriesCompletionOrder === 'strict' &&
    isItemDisabled(seriesIndex, completedSeriesIndexes);

  const items = moduleSeries.map((series, seriesIdx) => {
    const isExpanded = expandedSeries === series.fields.slug;
    const isActiveSeries = activeSeries === series;
    const isSeriesDisabled = shouldDisableSeries(seriesIdx);

    return (
      <li key={series.fields.slug}>
        <div
          className={classes.seriesContainer}
          aria-checked={isExpanded}
          role="switch"
          onClick={() => onSeriesCollapse(series, seriesIdx, isSeriesDisabled)}
          tabIndex={-1}
        >
          <Typography
            className={classes.seriesTitle}
            color={isExpanded ? 'textSecondary' : 'textPrimary'}
          >
            {series.fields.title}
          </Typography>
          <div className={classes.lengthContainer}>
            {series.fields.time && (
              <Typography
                component="span"
                className={classes.seriesTime}
                color="textPrimary"
              >
                {series.fields.time}{' '}
                {getDurationLabel(resourceItemPageSiteCopy, series.fields.time)}
              </Typography>
            )}
            <ArrowUpIcon />
          </div>
        </div>
        <Collapse
          classes={{ wrapperInner: classes.wrapperInner }}
          in={isExpanded}
        >
          {_get(series.fields, 'resources', []).map((resource, idx) => (
            <SeriesSmallCard
              key={resource.sys.id}
              disabled={isSeriesDisabled}
              item={resource}
              index={idx}
              resourceItemPageSiteCopy={resourceItemPageSiteCopy}
              active={isActiveSeries && activeResourceIdx === idx}
              onClick={
                isSeriesDisabled
                  ? handleSeriesResourceClick(seriesIdx)
                  : handleSeriesResourceClick(seriesIdx)
              }
              completed={_get(
                seriesProgress,
                [series.sys.id, 'completedResourcesIds'],
                [],
              ).includes(resource.sys.id)}
              Component={CourseDefaultCard}
            />
          ))}
          {showRecommendedTab(series) && (
            <Button
              variant="text"
              color="primary"
              className={classes.recommendedButton}
              disabled={!completedSeriesIds.includes(series.sys.id)}
              onClick={() => onRecommendedTabClick(seriesIdx)}
            >
              {_get(coursesSiteCopy, 'pageCopy.recommendedResources')}
            </Button>
          )}
        </Collapse>
      </li>
    );
  });

  return <ul className={classes.queueList}>{items}</ul>;
};
